<template>
    <div class="manage-users client-user">
        <div v-if="local_users.length > 1">
            <h2 class="mb-2">Agency Users</h2>
            <p class="mb-3">The users below are those associated with your agency. Unless they are set as an "Agency Admin," they will only have access to clients they are associated with. When you delete a user, they will lose access to all clients.</p>
            <div class="row mb-3">
                <div class="col-md-6 col-12">
                    <div class="card">
                        <div class="card-body">
                            <div class="mb-3">
                                <label class="form-label">Select a user</label>
                                <select 
                                    class="form-select" 
                                    name="user" 
                                    v-model="user_pos" 
                                    @update:modelValue="clearErrors"
                                 aria-label="Select a user">
                                    <option 
                                        v-for="(u, pos) in local_users" 
                                        :key="pos" 
                                        :value="pos"
                                    >
                                        {{ pos === 0 ? "Add a New User" : u.name + (u.agency != null ? ("(" + u.agency.name + ")") : "") }}
                                    </option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-else>
            <h2 class="mb-2">{{ (user_pos > 0 || (local_users.length === 1 && local_users[0].id > 0)) ? "Update" : "Add" }} Team Member</h2>
        </div>

        <div class="card mb-3">
            <div class="card-body">
                <h3>User Information</h3>
                <div class="d-md-flex d-block">
                    <div class="profile-image flex-shrink-1 px-2 pe-4">
                        <div class="profile-image-container mb-3">
                            <img 
                                :src="local_users[user_pos].image || '/img/images/profile_image_placeholder.png'" 
                                :alt="local_users[user_pos].name || 'User'" 
                                :key="local_users[user_pos].image || 'placeholder'" 
                                class="profile-image"
                            >
                            <button 
                                type="button" 
                                class="btn btn-secondary btn-icon-only-sm" 
                                @click="openImageModal" 
                                data-bs-toggle="tooltip"
                                title="Change Profile Image"
                            >
                                <img src="/img/icons/avid-icons/upload.svg" alt="Change Profile Image" class="icon">
                            </button>
                        </div>
                        <div class="text-danger invalid-feedback" v-if="errors.image !== ''">{{ errors.image }}</div>
                    </div>

                    <div class="mt-md-0 mt-3 px-2 w-100">
                        <!-- Name -->
                        <div class="row">
                            <div class="col-md-6 col-12 mb-3">
                                <label class="form-label">First Name</label>
                                <input 
                                    type="text" 
                                    class="form-control" 
                                    :class="{ 'is-invalid': errors.first_name }"
                                    name="first_name" 
                                    v-model="local_users[user_pos].first_name"
                                    @update:modelValue="errors.first_name = ''"
                                 aria-label="First Name">
                                <div class="text-danger invalid-feedback" v-show="errors.first_name">
                                    {{ errors.first_name }}
                                </div>
                            </div>
                            <div class="col-md-6 col-12 mb-3">
                                <label class="form-label">Last Name</label>
                                <input 
                                    type="text" 
                                    class="form-control" 
                                    :class="{ 'is-invalid': errors.last_name }"
                                    name="last_name" 
                                    v-model="local_users[user_pos].last_name"
                                    @update:modelValue="errors.last_name = ''"
                                 aria-label="Last Name">
                                <div class="text-danger invalid-feedback" v-show="errors.last_name">
                                    {{ errors.last_name }}
                                </div>
                            </div>
                        </div>
                        <div class="mb-3" v-if="Array.isArray(agencies)">
                            <label class="form-label">Agency</label>
                            <select 
                                class="form-select" 
                                v-model="local_users[user_pos].agency_id"
                             aria-label="Agency">
                                <option 
                                    v-for="a in agencies" 
                                    :key="a.id" 
                                    :value="a.id"
                                >
                                    {{ a.name }}
                                </option>
                            </select>
                        </div>
                        <div class="mb-3">
                            <label class="form-label">Email</label>
                            <input 
                                type="text" 
                                class="form-control" 
                                :class="{ 'is-invalid': errors.email }"
                                name="email" 
                                v-model="local_users[user_pos].email"
                                @update:modelValue="validateEmail"
                             aria-label="Email">
                            <div class="text-danger invalid-feedback" v-show="errors.email">
                                {{ errors.email }}
                            </div>
                        </div>
                        <div class="mb-3">
                            <label class="form-label">User Role</label>
                            <select 
                                class="form-select" 
                                v-model="local_users[user_pos].user_type"
                                :class="{ 'is-invalid': errors.user_type }"
                                @update:modelValue="errors.user_type = ''"
                             aria-label="User Role">
                                <option value="admin" v-if="user.user_type === 'admin'">Avid Admin</option>
                                <option value="agency_admin">Admin</option>
                                <option value="user">User</option>
                            </select>
                            <div class="text-danger invalid-feedback" v-show="errors.user_type">
                                {{ errors.user_type }}
                            </div>
                        </div>
                        <div class="mb-3" v-if="user.user_type === 'admin'">
                            <label class="form-label">Password <em>(Optional)</em></label>
                            <input 
                                type="text" 
                                class="form-control" 
                                v-model="local_users[user_pos].pw_update"
                             aria-label="Pw Update">
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Image Edit Modal -->
        <image-edit-modal
            :id="'image-edit-modal-user'"
            :current-image="local_users[user_pos].image"
            @image-updated="handleImageUpdate"
            ref="imageEditModalUser"
        />
        <div class="card mb-3">
            <div class="card-body">
                <h3>Clients</h3>
                <div class="row">
                    <div class="col-sm-5">
                        <label class="form-label">Client Name</label>
                        <select 
                            class="form-select" 
                            id="client-select" 
                            v-model="role.client" 
                            placeholder="Client"
                            :class="{ 'is-invalid': errors.clients }"
                         aria-label="Client Name">
                            <option value="" disabled selected hidden>Select the client...</option>
                            <option 
                                v-for="i in local_clients" 
                                :key="i.id" 
                                :value="i.id"
                                :disabled="i.pivot && i.pivot.access && i.pivot.access['settings']['edit'] === false"
                            >
                                {{ i.name }}
                                <span v-if="i.pivot && i.pivot.access && i.pivot.access['settings']['edit'] === false" class="text-muted"> (No Edit Access)</span>
                            </option>
                        </select>
                    </div>
                    <div class="col-sm-2">
                        <label class="form-label w-100">&nbsp;</label>
                        <button :disabled="!role.client"
                            type="button" 
                            class="btn btn-secondary"
                            @click.prevent="addRole"
                        >
                            <span><i class="fas fa-fw fa-plus"></i>Add</span>
                        </button>
                    </div>
                </div>
                <div class="text-danger invalid-feedback" v-show="errors.clients">
                    {{ errors.clients }}
                </div>
                <div class="row mt-3" v-if="local_users[user_pos].clients.length > 0">
                    <div class="col">
                        <span 
                            class="badge badge-secondary mb-1 me-2" 
                            v-for="client in local_users[user_pos].clients" 
                            :key="client.id"
                        >
                            {{ client.name }}
                            <i class="fa fa-remove me-0 ms-2" id="remove-role" @click="removeClient(client.id)"></i>
                        </span>
                    </div>
                </div>
            </div>
        </div>
        <div class="row">
            <div class="col">
                <!-- Update Button -->
                <div class="mb-3">
                    <button 
                        type="submit" 
                        class="btn btn-primary"
                        @click.prevent="update"
                        :disabled="form.busy"
                    >
                        <img class="icon me-2" src="/img/icons/avid-icons/save.svg" alt=""> 
                        {{ (user_pos > 0 || (local_users.length === 1 && local_users[0].id > 0)) ? "Update" : "Add Team Member" }}
                    </button>
                    <button 
                        type="button" 
                        class="btn btn-warning ms-2" 
                        @click.prevent="deleteUser"
                        :disabled="form.busy"
                        v-if="user_pos > 0 || (local_users.length === 1 && local_users[0].id > 0)"
                    >
                        <i class="fa fa-trash"></i> Delete
                    </button>
                    <button 
                        type="button" 
                        class="btn btn-white ms-2" 
                        :disabled="form.busy"
                        @click.prevent="back" 
                        v-if="local_users.length === 1"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import Swal from 'sweetalert2';
    import ImageDropzone from '../components/ImageDropzone.vue';
    import ImageEditModal from '../components/ImageEditModal.vue';

    export default {
        name: 'ManageUsers',
        components: {
            ImageDropzone,
            ImageEditModal,
            Swal
        },
        props: {
            user: {
                type: Object,
                required: true
            },
            clients: {
                type: Array,
                required: true
            },
            users: {
                type: [Array, Object],
                required: true
            },
            agencies: {
                type: [Array, Object],
                required: true
            }
        },
        data() {
            return {
                form: {
                    successful: false,
                    busy: false,
                    error: false
                },
                errors :{
                    email: "",
                    first_name: "",
                    last_name: "",
                    user_type: "",
                    clients: ""
                },
                user_pos: 0,
                local_users: [],
                role: {
                    client: ""
                },
                local_clients: []
            };
        },
        beforeMount() {
            this.local_clients = this.clients;
            //Sort local clients by the name attribute
            this.local_clients.sort((a, b) => (a.name > b.name) ? 1 : -1);
            //If the users is an object
            if(Array.isArray(this.users) == false){
                this.local_users = [this.users];
                //If it is a new user
                if(this.local_users[0].clients == null){
                    this.local_users[0]= this.getNewUser();
                    this.local_users[0].agency_id = this.agencies.id;
                }
            }else{
                this.local_users = this.users;
                this.local_users.unshift(this.getNewUser());
            }
        },
        methods: {
            getNewUser() {
                return {
                        id: -1,
                        url: null,
                        name: "",
                        first_name: "",
                        last_name: "",
                        organization: "",
                        agency_id: -1,
                        email: "",
                        photo_url: "",
                        image: "/img/icons/avid-icons/user-edit.svg",
                        user_type: "",
                        clients:[],
                        pw_update: ""
                    };
            },
            clearErrors() {
                this.errors.email = "";
                this.errors.first_name = "";
                this.errors.last_name = "";
                this.errors.user_type = "";
                this.errors.clients = "";
            },
            validateEmail() {
                var self = this;
                window.axios.post('/api/user/check_email', this.local_users[this.user_pos])
                  .then(response => {
                    if(response.status == 200) {
                        if(response.data.exists) {
                            self.errors.email = 'The email entered is already attached to a user at a different agency.';
                        }
                        else
                            self.errors.email = '';
                    }
                  });
            },
            checkErrors() {
                if(this.local_users[this.user_pos].first_name == null || this.local_users[this.user_pos].first_name == "")
                    this.errors.first_name = "First name is required.";
                else
                    this.errors.first_name = "";

                if(this.local_users[this.user_pos].last_name == null || this.local_users[this.user_pos].last_name == "")
                    this.errors.last_name = "Last name is required.";
                else
                    this.errors.last_name = "";

                if(this.local_users[this.user_pos].user_type == null || this.local_users[this.user_pos].user_type == "")
                    this.errors.user_type = "User type is required.";
                else
                    this.errors.user_type = "";

                if(this.local_users[this.user_pos].email == null || this.local_users[this.user_pos].email == "")
                    this.errors.email = "Email is required.";
                else if(this.errors.email != 'The email entered is already attached to a different agency.')
                    this.errors.email = "";

                // Clients are optional
                this.errors.clients = "";
            },
            update() {
                var self = this
                this.form.error = false;

                this.checkErrors();
                if(this.hasError()) {
                    this.form.error = true;
                    return;
                }

                //If it is an agency making the user
                if(Array.isArray(this.agencies) == false) {
                    this.local_users[this.user_pos].agency_id = this.agencies.id;
                    this.local_users[this.user_pos].organization = this.agencies.name;
                }
                else if(this.local_users[this.user_pos].agency_id != -1)
                    //Set the organization to be the agency name they have selected
                    for(let i = 0; i < this.agencies.length; i++){
                        if(this.agencies[i].id == this.local_users[this.user_pos].agency_id) {
                            this.local_users[this.user_pos].organization = this.agencies[i].name;
                            break;
                        }
                    }


                this.form.busy = true;

                //Make a new user
                if(this.local_users[this.user_pos].id == -1) {

                    window.axios.post('/api/user', this.local_users[this.user_pos])
                      .then(response => {
                        self.form.busy = false;

                        const userName = response.data.data.first_name || response.data.data.name || 'User';
                        Swal.fire({
                            title: "User created!",
                            text: userName + " has been created.",
                            icon: "success",
                            showCancelButton: false,
                            confirmButtonText: "OK",
                            customClass: {
                                popup: 'swal-success'
                            },
                        }).then((result) => {
                            if (result.isConfirmed) {

                                if(this.local_users.length == 1){
                                    //If there is a referring url that matches '/admin/agency/team' or /admin/agencies/###/team, go back to that referring url
                                    let referrer = document.referrer;
                                    if (referrer && (referrer.includes('/admin/agency/team') || referrer.match(/\/admin\/agencies\/\d+\/team/))) {
                                        window.location.href = referrer;
                                        return;
                                    } 
                                    else if (this.user.agency_id != null && this.user.user_type.indexOf('admin') != -1) {
                                        window.location.href = '/admin/agencies/' + this.user.agency_id + '/team';
                                        return;
                                    }
                                    window.location.href = '/admin/agency/team';
                                    return;
                                } else {
                                    //Add the new user to the list and set the dropdown
                                    self.local_users.push(response.data.data);

                                    //Reset the "New User" option
                                    self.local_users.shift();
                                    self.local_users.unshift(self.getNewUser());

                                    self.user_pos = self.local_users.length -1;

                                    self.form.successful = true;
                                    setTimeout( function() {self.form.successful = false;} , 5000);
                                }
                            }
                        });
                      }).catch(error => {
                        // Handle errors here
                        if (error.response.status === 422) {
                          self.form.error = true;
                          self.errors.email = 'The email entered is not in the correct format.';
                        }
                        // Handle errors here
                        else if (error.response.status === 412) {
                          self.form.error = true;
                          self.errors.email = 'The user is a part of a different organization. You can only be a part of one group.';
                        }
                      });
                }
                else {
                    window.axios.put('/api/user/' + this.local_users[this.user_pos].id, this.local_users[this.user_pos])
                      .then(response => {
                        self.form.busy = false;
                        Swal.fire({
                            title: "User updated!",
                            text: self.local_users[self.user_pos].name + " has been updated.",
                            icon: "success",
                            showCancelButton: false,
                            confirmButtonText: "OK",
                            customClass: {
                                popup: 'swal-success'
                            },
                        }).then((result) => {
                            if (result.isConfirmed) {
                                if(this.local_users.length == 1){
                                    let referrer = document.referrer;
                                    if (referrer && (referrer.includes('/admin/agency/team') || referrer.match(/\/admin\/agencies\/\d+\/team/))) {
                                        window.location.href = referrer;
                                        return;
                                    } 
                                    else if (this.user.agency_id != null && this.user.user_type.indexOf('admin') != -1) {
                                        window.location.href = '/admin/agencies/' + this.user.agency_id + '/team';
                                        return;
                                    }
                                    window.location.href = '/admin/agency/team';
                                    return;
                                } else {
                                    self.form.busy = false;
                                    self.form.successful = true;
                                    setTimeout( function() {self.form.successful = false} , 5000);
                                }
                            }
                        });
                    }).catch(error => {
                        // Handle errors here
                        if (error.response.status === 422) {
                          self.form.error = true;
                          self.errors.email = 'The email entered is not in the correct format.';
                        }
                    });
                }
            },
            hasError() {
                return this.errors.email != "" || this.errors.first_name != ""  || this.errors.last_name != ""|| this.errors.user_type != "";
            },
            s3UploadSuccess(s3ObjectLocation){
                //Stupid Amazon URL encoding my folder
                s3ObjectLocation = s3ObjectLocation.replace("%2F", "/");
                s3ObjectLocation = s3ObjectLocation.startsWith("http") ? s3ObjectLocation : 'https:'+ s3ObjectLocation;

                this.local_users[this.user_pos].photo_url = s3ObjectLocation;
                this.local_users[this.user_pos].image = s3ObjectLocation;
                //Only call this if they've saved the client already
                // if(this.local_users[this.user_pos].id != -1)
                //     this.update();
            },
            handleImageUpdate(value) {
                if (value === '') {
                    // Image was deleted - just update the local state
                    this.local_users[this.user_pos].photo_url = null;
                    this.local_users[this.user_pos].image = null;
                } else {
                    // New image was uploaded
                    this.s3UploadSuccess(value);
                }
            },
            openImageModal() {
                const modalElement = document.getElementById('image-edit-modal-user');
                if (modalElement) {
                    const modal = new window.bootstrap.Modal(modalElement);
                    modal.show();
                }
            },
            removeClient(client_id) {

                //Now remove them from the user array.
                for(var i = 0; i < this.local_users[this.user_pos].clients.length; i++ ){
                    if(this.local_users[this.user_pos].clients[i].id == client_id ) {
                        this.local_users[this.user_pos].clients.splice(i, 1);
                        break;
                    }
                }
            },
            addRole() {
                var self = this;

                if(this.role.client == "")
                    return;

                //Make users an array (just in case)
                if(this.local_users[this.user_pos].clients == null)
                    this.local_users[this.user_pos].clients = [];

                //See if the role already exists before trying to add a new one
                for(var i = 0; i < this.local_users[this.user_pos].clients.length; i++ ){
                    if(this.local_users[this.user_pos].clients[i].id == this.role.client )
                        //If it does exist, just exit the function
                        return;
                }

                this.errors.clients = "";

                //Find the user's name
                var client_name = "";
                for(var i = 0; i < this.local_clients.length; i++)
                    if(this.local_clients[i].id == this.role.client)
                        client_name = this.local_clients[i].name;

                //Add them to the "users" array
                this.local_users[this.user_pos].clients.push({
                    id: this.role.client,
                    name: client_name
                })

                //Clear the drop down menus
                this.role.client = "";
            },
            deleteUser() {
                var self = this;

                Swal.fire({
                  title: "Are you sure you want to delete " + self.local_users[self.user_pos].name + "?",
                  text: "This user will be removed from all clients and will no longer be able to log into Avid.",
                  icon: "warning",
                  showCancelButton: true,
                  confirmButtonText: "Yes, delete",
                  customClass: {
                    popup: 'swal-warning'
                  },
                }).then((result) => {
                  if (result.isConfirmed) {
                    window.axios.delete('/api/user/' + self.local_users[self.user_pos].id)
                        .then(response => {
                            if (response.status == 200) {
                                //self.getSystemArray(system.system_id).$remove(system);
                                //console.log('deleted',response.data);
                                Swal.fire({
                                    title: "Deleted!",
                                    text: self.local_users[self.user_pos].name + " has been deleted.",
                                    icon: "success",
                                    customClass: {
                                        popup: 'swal-success'
                                    },
                                }).then((result) => {
                                    self.local_users.splice(self.user_pos, 1);
                                    self.user_pos = 0;

                                });
                            }
                        });
                  } else if (result.isDenied) {

                  }
                });
            },
            back() {
                const agency_id = this.local_users[this.user_pos].agency_id;

                if(this.user.user_type != 'admin') 
                    window.location.href = '/admin/agency/team';
                else
                    window.location.href = '/admin/agencies/' + agency_id + '/team';
            },
        }
    }
</script>
